import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React, {useCallback, useEffect} from 'react';

import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {NodeDetailsSidepanelContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/NodeDetailsSidepanelContext';
import {ContextPanelContent, tabPanelStyles} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import {useFlowchartSidepanelManagerContext} from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelUnsavedChangesContext';
import {Tab, TabContainer} from 'ui-library/TabContainer';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {
  TaskNodeDetailsSidepanelContentProps,
  SidepanelTab,
  NodeSettingsTabKeys,
} from '../processFlowchartConfigEditorConstants';
import {isInteractiveTaskNode, TaskSchemaNode} from '../processFlowchartConfigEditorNodeTypes';

function filterTabsForSelectedNode(
  tabs: Array<SidepanelTab>,
  selectedNode: TaskSchemaNode,
  selectedFieldId: string | null
): Array<SidepanelTab> {
  return tabs.filter((tab) => {
    if (selectedFieldId && tab.key !== NodeSettingsTabKeys.FIELD_MAPPING_INFO) {
      return false;
    } else if (!selectedFieldId && tab.key === NodeSettingsTabKeys.FIELD_MAPPING_INFO) {
      return false;
    }

    if (
      tab.key === NodeSettingsTabKeys.FLOWCHART_SETTINGS &&
      selectedNode.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
    ) {
      // There are no transitions into the root node so the FLOWCHART_SETTINGS tab is not relevant.
      return false;
    }
    return true;
  });
}

export default function TaskNodeDetailsSidepanelContent(props: {
  nodeDetailsSidepanelContentProps: TaskNodeDetailsSidepanelContentProps;
  tabs: Array<SidepanelTab>;
}): JSX.Element {
  const {nodeDetailsSidepanelContentProps, tabs} = props;
  const {selectedNode, allSchemasById} = NodeDetailsSidepanelContext.useContext();
  const sidepanelManager = useFlowchartSidepanelManagerContext();

  const effectiveState = sidepanelManager.getEffectiveState();
  const activeTabId = effectiveState.activeTabState?.activeTabId;

  assert(isInteractiveTaskNode(selectedNode), 'Expected selected node to be a task node');

  const visibleTabs = filterTabsForSelectedNode(
    tabs,
    selectedNode,
    nodeDetailsSidepanelContentProps.selectedFieldId
  );
  assert(visibleTabs.length > 0, 'Expected at least one visible tab');
  const defaultTabKey = visibleTabs[0].key;
  const nodeFieldDetailsTab = visibleTabs.find((tab) => tab.openOnFieldSelect);

  // Initialize tab only when activeTabId is not set
  // Don't reset if it's set to something we don't recognize - it might be from a nested component (like MethodEditor)
  useEffect(() => {
    if (activeTabId == null && visibleTabs.length > 0) {
      sidepanelManager.changeTab({activeTabId: defaultTabKey, nestedActiveTabId: null});
    }
  }, [activeTabId, defaultTabKey, sidepanelManager, visibleTabs.length]);

  // Handle field selection override - update active tab when field is selected
  useEffect(() => {
    if (nodeDetailsSidepanelContentProps.selectedFieldId && nodeFieldDetailsTab) {
      const fieldTabKey = nodeFieldDetailsTab.key;
      if (fieldTabKey !== activeTabId) {
        sidepanelManager.changeTab({activeTabId: fieldTabKey, nestedActiveTabId: null});
      }
    }
  }, [nodeDetailsSidepanelContentProps.selectedFieldId, nodeFieldDetailsTab, activeTabId, sidepanelManager]);

  const handleRequestTabChange = useCallback(
    (tab: string) => {
      sidepanelManager.changeTab({activeTabId: tab, nestedActiveTabId: null});
    },
    [sidepanelManager]
  );

  // Determine which tab to display
  // There is only ever one tab when a field is selected. If this changes we should break
  // field-specific metadata into its own sidepanel.
  let activeTab: string;
  if (nodeDetailsSidepanelContentProps.selectedFieldId && nodeFieldDetailsTab) {
    // Field selection overrides the active tab
    activeTab = nodeFieldDetailsTab.key;
  } else if (activeTabId != null && visibleTabs.some((tab) => tab.key === activeTabId)) {
    // Use the active tab from state if it's set and still visible
    activeTab = activeTabId;
  } else {
    // Fallback to default if no active tab is set or it's no longer visible
    activeTab = defaultTabKey;
  }

  const taskSchemaId = selectedNode.data.taskSchemaId;
  const taskSchema = allSchemasById[taskSchemaId] as ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  assert(taskSchema != null, `Task schema not found in allSchemasById: ${taskSchemaId}`);
  const isSchemaArchived = selectedNode.data.isArchived ?? false;
  const isSchemaNotWritable = !taskSchema.permissions.writable;
  const computedLocked = nodeDetailsSidepanelContentProps.locked || isSchemaArchived || isSchemaNotWritable;
  const propsWithComputedLocked = {
    ...nodeDetailsSidepanelContentProps,
    locked: computedLocked,
  };

  if (visibleTabs.length === 1) {
    const tab = visibleTabs[0];
    return (
      <ContextPanelContent>
        <tab.content {...propsWithComputedLocked} />
      </ContextPanelContent>
    );
  } else {
    return (
      <TabContainer<string>
        activeTab={activeTab}
        onRequestTabChange={handleRequestTabChange}
        headerDividerClassName={css(styles.sidepanelHeaderDividerStyle)}
        paneClassName={css(tabPanelStyles.noSpacing)}
      >
        {visibleTabs.map((tab) => (
          <Tab
            key={tab.key}
            title={<div className={css(styles.tabTitleStyle)}>{tab.label}</div>}
            tabKey={tab.key}
          >
            <ContextPanelContent>
              <tab.content {...propsWithComputedLocked} />
            </ContextPanelContent>
          </Tab>
        ))}
      </TabContainer>
    );
  }
}

const styles = StyleSheet.create({
  tabTitleStyle: {
    display: 'flex',
    fontSize: '12px',
    paddingTop: '12px',
    paddingBottom: '6px',
    marginLeft: '12px',
    marginRight: '12px',
  },
  sidepanelHeaderDividerStyle: {
    borderTop: `1px solid ${Color.GRAY_300}`,
  },
  noFieldSelectedText: {
    fontSize: '16px',
    color: Color.GRAY_900,
  },
  temp: {
    margin: 0,
    padding: 0,
  },
});
